import * as React from 'react';
import {
  ColumnDef,
  ColumnFiltersState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { ArrowUpDown, ArrowUp, ArrowDown, Search, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  searchPlaceholder?: string;
  searchColumn?: string;
  className?: string;
  onRowClick?: (row: TData) => void;
  getRowClassName?: (row: TData) => string;
  hideSearch?: boolean;
  /** Renders before the search box (e.g. filters) */
  toolbarAddon?: React.ReactNode;
  /**
   * Below the `md` breakpoint (768px), these column ids are hidden to reduce horizontal scrolling.
   * Ignored when empty or omitted.
   */
  columnIdsHiddenWhenNarrow?: string[];
  /** Tighter cell padding for large data tables (e.g. attendance list). */
  dense?: boolean;
  /**
   * Below `md`, render this column in a second table row with `colSpan` so it spans the full width
   * (e.g. action buttons along the bottom of each record on mobile).
   */
  stackActionColumnOnNarrow?: string;
  /**
   * Always merged into column visibility (e.g. hide synthetic columns used only for global search).
   * Survives updates from `columnIdsHiddenWhenNarrow`.
   */
  columnVisibilityDefaults?: VisibilityState;
  /** Distribute visible columns evenly across the table width (`table-layout: fixed`). */
  equalColumnWidths?: boolean;
}

export function DataTable<TData, TValue>({
  columns,
  data,
  searchPlaceholder = 'Search...',
  searchColumn,
  className,
  onRowClick,
  getRowClassName,
  hideSearch = false,
  toolbarAddon,
  columnIdsHiddenWhenNarrow,
  dense = false,
  stackActionColumnOnNarrow,
  columnVisibilityDefaults,
  equalColumnWidths = false,
}: DataTableProps<TData, TValue>) {
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>([]);
  const [columnVisibility, setColumnVisibility] = React.useState<VisibilityState>({});
  const [globalFilter, setGlobalFilter] = React.useState('');

  const narrowIdsKey = columnIdsHiddenWhenNarrow?.length
    ? [...columnIdsHiddenWhenNarrow].sort().join('|')
    : '';

  const [isWideLayout, setIsWideLayout] = React.useState(() => {
    if (typeof window === 'undefined') {
      return true;
    }
    return window.matchMedia('(min-width: 768px)').matches;
  });

  React.useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const onChange = () => setIsWideLayout(mq.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  React.useEffect(() => {
    const base: VisibilityState = { ...(columnVisibilityDefaults ?? {}) };
    if (!narrowIdsKey) {
      setColumnVisibility(base);
      return;
    }
    const ids = narrowIdsKey.split('|');
    const next: VisibilityState = { ...base };
    for (const id of ids) {
      next[id] = isWideLayout;
    }
    setColumnVisibility(next);
  }, [isWideLayout, narrowIdsKey, columnVisibilityDefaults]);

  const table = useReactTable({
    data,
    columns,
    getCoreRowModel: getCoreRowModel(),
    onSortingChange: setSorting,
    getSortedRowModel: getSortedRowModel(),
    onColumnFiltersChange: setColumnFilters,
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onGlobalFilterChange: setGlobalFilter,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      globalFilter,
    },
  });

  const layoutColumns = React.useMemo(() => {
    const visible = table.getVisibleLeafColumns();
    if (stackActionColumnOnNarrow && !isWideLayout) {
      return visible.filter((col) => col.id !== stackActionColumnOnNarrow);
    }
    return visible;
  }, [table, stackActionColumnOnNarrow, isWideLayout, columnVisibility]);

  const equalColWidth =
    layoutColumns.length > 0 ? `${100 / layoutColumns.length}%` : undefined;

  return (
    <div className={cn('space-y-4', className)}>
      {/* Global Search */}
      {!hideSearch && (
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-end">
          {toolbarAddon}
          <div className="relative w-full min-w-0 sm:flex-1 sm:min-w-[200px] sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder={searchPlaceholder}
              value={globalFilter ?? ''}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="pl-9 pr-9"
            />
            {globalFilter && (
              <button
                onClick={() => setGlobalFilter('')}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
          {table.getFilteredRowModel().rows.length > 0 && (
            <div className="shrink-0 text-sm text-muted-foreground sm:ml-auto">
              {table.getFilteredRowModel().rows.length}{' '}
              {table.getFilteredRowModel().rows.length === 1 ? 'result' : 'results'}
            </div>
          )}
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto rounded-xl border border-border [-webkit-overflow-scrolling:touch] [touch-action:pan-x_pan-y]">
        <table
          className={cn('w-full text-sm', equalColumnWidths && 'table-fixed')}
        >
          {equalColumnWidths && equalColWidth && (
            <colgroup>
              {layoutColumns.map((col) => (
                <col key={col.id} style={{ width: equalColWidth }} />
              ))}
            </colgroup>
          )}
          <thead className="bg-muted/60">
            {table.getHeaderGroups().map((headerGroup) => (
              <tr key={headerGroup.id} className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                {headerGroup.headers.map((header) => (
                  <th
                    key={header.id}
                    className={cn(
                      'font-semibold',
                      dense ? 'px-1.5 py-1.5 sm:px-2.5 sm:py-2' : 'px-2 py-2.5 sm:px-4 sm:py-3',
                      equalColumnWidths && 'break-words',
                      stackActionColumnOnNarrow &&
                        header.column.id === stackActionColumnOnNarrow &&
                        'hidden md:table-cell',
                    )}
                  >
                    {header.isPlaceholder ? null : (
                      <div className="flex items-center gap-2">
                        <div className="flex-1">
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </div>
                        {header.column.getCanSort() && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-6 w-6 p-0 hover:bg-muted"
                            onClick={() => header.column.toggleSorting()}
                          >
                            {header.column.getIsSorted() === 'asc' ? (
                              <ArrowUp className="h-3.5 w-3.5" />
                            ) : header.column.getIsSorted() === 'desc' ? (
                              <ArrowDown className="h-3.5 w-3.5" />
                            ) : (
                              <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
                            )}
                          </Button>
                        )}
                      </div>
                    )}
                  </th>
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => {
                const cells = row.getVisibleCells();
                const narrowStack =
                  Boolean(stackActionColumnOnNarrow) && !isWideLayout;
                const mainCells = narrowStack
                  ? cells.filter((c) => c.column.id !== stackActionColumnOnNarrow)
                  : cells;
                const actionCell = narrowStack
                  ? cells.find((c) => c.column.id === stackActionColumnOnNarrow)
                  : undefined;

                return (
                  <React.Fragment key={row.id}>
                    <tr
                      onClick={() => onRowClick?.(row.original)}
                      className={cn(
                        'border-b transition-colors hover:bg-muted/40',
                        narrowStack && 'border-b-0',
                        !narrowStack && 'last:border-0',
                        onRowClick && 'cursor-pointer',
                        getRowClassName?.(row.original),
                      )}
                    >
                      {mainCells.map((cell) => (
                        <td
                          key={cell.id}
                          className={cn(
                            'align-top',
                            dense ? 'px-1.5 py-1.5 sm:px-2.5 sm:py-2' : 'px-2 py-3 sm:px-4 sm:py-4',
                            equalColumnWidths && 'break-words',
                          )}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </td>
                      ))}
                    </tr>
                    {actionCell && (
                      <tr
                        key={`${row.id}-actions`}
                        className="border-b border-border bg-muted/20 last:border-0 md:hidden"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <td
                          colSpan={mainCells.length}
                          className={cn(
                            'align-middle',
                            dense ? 'px-1.5 py-2 sm:px-2.5' : 'px-2 py-2.5 sm:px-4',
                          )}
                        >
                          {flexRender(
                            actionCell.column.columnDef.cell,
                            actionCell.getContext(),
                          )}
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })
            ) : (
              <tr>
                <td colSpan={columns.length} className="px-4 py-8 text-center text-muted-foreground">
                  No results found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Helper component for sortable column headers
export function SortableHeader({ children, column }: { children: React.ReactNode; column: any }) {
  return (
    <div className="flex items-center gap-2 cursor-pointer select-none" onClick={() => column.toggleSorting()}>
      {children}
      <div className="ml-auto">
        {column.getIsSorted() === 'asc' ? (
          <ArrowUp className="h-3.5 w-3.5" />
        ) : column.getIsSorted() === 'desc' ? (
          <ArrowDown className="h-3.5 w-3.5" />
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 opacity-50" />
        )}
      </div>
    </div>
  );
}

